import { useEffect, useState } from 'react';
import { Loader } from '@piggytechng/component-library';
import { formatToNaira } from '../../../../../../helpers/utils';
import { SafeLockSidesheetProps } from '../../../../../../types';
import useSafeLock from '../../../../../../hooks/useSafeLock';
import './SafeLockSidesheet.scss';
import CloseButton from '../../../../../../assets/icons/close-button.svg';
import { formatDate } from '../../../../../../helpers/utils/date';

const SafeLockSidesheet = ({ userId, onClose }: SafeLockSidesheetProps) => {
  const [activeCurrency, setActiveCurrency] = useState<'ngn' | 'usd'>('ngn');
  const [activeListTab, setActiveListTab] = useState<'ongoing' | 'paid-back'>('ongoing');
  const {
    isLoading,
    safelockBalances,
    completedSafelocks,
    pendingSafelocks,
    fetchSafelockBalance,
    fetchCompletedSafelocks,
    fetchPendingSafelocks,
    fetchSafelockData,
    singleSafelockData,
    fetchSafelockTransactions,
    safelockTransactions,
  } = useSafeLock();
  const [selectedSafelockId, setSelectedSafelockId] = useState<string | null>(null);
  const [selectedSafelockExtra, setSelectedSafelockExtra] = useState<any | null>(null);

  async function handleSelectTab(clickedTab: 'ongoing' | 'paid-back') {
    if (clickedTab === activeListTab) return;

    if (clickedTab === 'ongoing') {
      await fetchPendingSafelocks(userId, activeCurrency, 1, 30);
    }

    if (clickedTab === 'paid-back') {
      await fetchCompletedSafelocks(userId, activeCurrency, 1, 30);
    }

    setActiveListTab(clickedTab);
  }

  async function handleCurrencyButton(selectedCurrency: 'ngn' | 'usd') {
    if (selectedCurrency === activeCurrency) return;

    if (activeListTab === 'ongoing') {
      await fetchPendingSafelocks(userId, selectedCurrency, 1, 30);
    }

    if (activeListTab === 'paid-back') {
      await fetchCompletedSafelocks(userId, selectedCurrency, 1, 30);
    }

    setActiveCurrency(selectedCurrency);
  }

  function formatAmount(value: number) {
    const balance = formatToNaira(value);
    if (activeCurrency === 'ngn') {
      return balance;
    } else {
      return balance.replace('₦', '$');
    }
  }

  function formatSafelockData(data: any) {
    const slData = {
      Initial_Deposit: formatAmount(data.asls_initial_amount),
      Total_Interest_Earned: formatAmount(data.asls_interest_amount),
      Payback_Date: formatDate(data.asls_paydate).readableDate,
      Start_Date: formatDate(data.asls_date).readableDate,
      Initial_Interest_Earned: data.asls_interest + '%',
      Status: data.asls_status === '0' ? 'Ongoing' : 'Matured',
      Safelock_ID: data.asls_rand,
      Days_Left: data.asls_paydate - data.asls_date,
      Payback_Time: formatDate(data.asls_paydate).time,
    };

    if (data.asls_paydate * 1000 < Date.now()) {
      slData.Days_Left = 0;
    } else {
      const daysleft = (data.asls_paydate * 1000 - Date.now()) / (1000 * 60 * 60 * 24);
      slData.Days_Left = parseInt(daysleft.toString().split('.')[0]);
    }

    const slUIdata = Object.entries(slData);
    return {
      slData,
      slUIdata,
    };
  }

  //TEMP IMPLEMENTATION
  // function formatSafelockAmount(amount: any) {
  //   if (amount.split('$')[0] === '') {
  //     return amount;
  //   }
  //   const split = amount.split('undefined');

  //   return '₦' + split[1];
  // }

  useEffect(() => {
    fetchSafelockBalance(userId);
    fetchCompletedSafelocks(userId, activeCurrency, 1, 30);
    fetchPendingSafelocks(userId, activeCurrency, 1, 30);
  }, []);

  return (
    <>
      <div className="safelock-sidesheet">
        {selectedSafelockId === null ? (
          <>
            <img src={CloseButton} className="close-btn" alt="" onClick={() => onClose()} />
            <div className="heading">
              <h3 className="title">Safelock</h3>
              <p className="text">Total Balance</p>
              <h1 className="balance">
                {formatAmount(
                  activeCurrency === 'ngn' ? safelockBalances.nairaBalance : safelockBalances.dollarBalance,
                )}
              </h1>
            </div>
            <div className="tabs">
              <div
                className={`ongoing ${activeListTab === 'ongoing' && 'selected'}`}
                onClick={() => handleSelectTab('ongoing')}
              >
                Ongoing
              </div>
              <div
                className={`paid-back ${activeListTab === 'paid-back' && 'selected'}`}
                onClick={() => handleSelectTab('paid-back')}
              >
                Paid Back
              </div>
            </div>
            <div className="currency-option">
              <span
                className={`option ${activeCurrency === 'ngn' && 'selected'}`}
                onClick={() => handleCurrencyButton('ngn')}
              >
                {'Naira - N250k'}
              </span>
              <span
                className={`option ${activeCurrency === 'usd' && 'selected'}`}
                onClick={() => handleCurrencyButton('usd')}
              >
                {'Dollar - $250k'}
              </span>
            </div>
            <div className="safelock-list">
              {activeListTab === 'ongoing' && (
                <div className="ongoing-safelock-list">
                  {pendingSafelocks.map(({ image, title, keyPoints, progress, progressText, safelockId }: any) => (
                    <div
                      className="safelock-list-item"
                      key={safelockId}
                      onClick={() => {
                        fetchSafelockData(safelockId);
                        setSelectedSafelockId(safelockId);
                        setSelectedSafelockExtra({
                          progressText,
                          progress,
                        });
                        fetchSafelockTransactions(userId, safelockId);
                      }}
                    >
                      <div className="safelock-image">
                        <img src={image} alt="safelock-image" />
                      </div>
                      <div className="safelock-data">
                        <p className="title">{title}</p>
                        <p className="amount">{keyPoints[0].value}</p>
                        <span className="status">{keyPoints[0].label}</span>
                        <div className="progress">
                          <div className="__bar">
                            <div className="__level" style={{ width: `${progress}%` }}></div>
                          </div>
                          <div className="__info">
                            <span>{progressText}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {activeListTab === 'paid-back' && (
                <div className="paid-back-safelock-list">
                  {completedSafelocks.map(({ image, title, keyPoints, progress, safelockId, progressText }: any) => (
                    <div
                      className="safelock-list-item"
                      key={safelockId}
                      onClick={() => {
                        fetchSafelockData(safelockId);
                        setSelectedSafelockId(safelockId);
                        fetchSafelockTransactions(userId, safelockId);
                      }}
                    >
                      <div className="safelock-image">
                        <img src={image} alt="safelock-image" />
                      </div>
                      <div className="safelock-data">
                        <p className="title">{title}</p>
                        <p className="amount">{keyPoints[0].value}</p>

                        <span className="status">{keyPoints[0].label}</span>
                        <div className="progress">
                          <div className="__bar">
                            <div className="__level" style={{ width: `${progress}%` }}></div>
                          </div>
                          <div className="__info">
                            <span>{progressText}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            {isLoading ? (
              <>
                <div className="page-loader">
                  <Loader color="#0F60D6" />
                </div>
              </>
            ) : (
              <>
                <div className="single-safelock-sidesheet">
                  <div className="top-container">
                    <div className="left-container">
                      <img src={CloseButton} className="close-btn" alt="" onClick={() => setSelectedSafelockId(null)} />
                    </div>
                  </div>
                  <div className="heading">
                    <h3 className="title">{singleSafelockData?.asls_title}</h3>
                    <p className="text">Total Balance</p>
                    <h1 className="balance">{formatAmount(singleSafelockData?.asls_amount)}</h1>
                  </div>
                  <div className="progress">
                    <div className="__bar">
                      <div className="__level" style={{ width: `${selectedSafelockExtra?.progress}%` }}></div>
                    </div>
                    <div className="__info">
                      <span>{formatSafelockData(singleSafelockData).slData.Days_Left} days left</span>
                    </div>
                  </div>
                  <div className="safelock-data">
                    {formatSafelockData(singleSafelockData).slUIdata.map((data) => {
                      return (
                        <div className="data-box" key={data[0]}>
                          <p>{data[0].replaceAll('_', ' ')}</p>
                          <h3>{data[1]}</h3>
                        </div>
                      );
                    })}
                  </div>
                  <div className="safelock-transactions">
                    <h3>Transactions</h3>
                    <div className="transactions-container">
                      {safelockTransactions?.map((val: any, index: any) => {
                        const { readableDate, time } = formatDate(val.date);

                        return (
                          <div className="transaction" key={index}>
                            <p>
                              <span>{formatToNaira(val?.amount)}</span>
                              {` added to savings`}
                            </p>
                            <p>
                              <span>Memo: Target</span>
                              {` ${val?.memo}`}
                            </p>
                            <p>
                              <span>Date: </span>
                              {` ${readableDate} ${time}`}
                            </p>
                            {val?.transactionId && (
                              <p className="transaction-id">
                                <span>Transaction ID:</span> {`${val.transactionId}`}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default SafeLockSidesheet;
